/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap,
	ISchemaIntegrationRefModel,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import { BEM, useTranslate } from "@adapptio/lib-components";

import { BlueprintActionNode_v2, NODE_TYPES, TBlueprintActionNodeTypes } from "@adapptio/lib-core";
import { FlowEditor, IFlowEditor } from "../../../../ui/components/FlowEditor/FlowEditor";
import { FLOW_EDITOR_LAYOUT, TGetNodeInfoFn } from "../../../../ui/components/FlowEditor/FlowEditorTypes";
import { Panel, PANEL_STYLE } from "../../../../ui/components/Panel";
import { useBlueprintModel } from "../../../../ui/hooks/useBlueprintModel";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";

import { ActionEditorView } from "../ActionEditorView";
import { ReactFlowInstance } from "@adapptio/vendor-react-flow-renderer";

export interface IActionEditorFlowProps {
	view: ActionEditorView;
}

export const ActionEditorFlow: React.FunctionComponent<IActionEditorFlowProps> = ({ view }) => {
	const t = useTranslate();

	const model = view.getActionModel();

	const [ nodesValue, setNodesValue ] = useState(model.getModel().props.spec.props.nodes.value);

	useSimpleEvent(model.onRender, () => setNodesValue(model.getModel().props.spec.props.nodes.value), [
		model
	]);

	const { model: flowNodeListModel } = useBlueprintModel(
		nodesValue as unknown as ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		"flowNodeList"
	);

	const editSelection = useMemo(() => model.getSelection(), [ model ]);

	const [ reactFlowInstance, setReactFlowInstance ] = useState<ReactFlowInstance>(null);

	const handleFlowSelectionChange = useCallback(
		(nodes: Array<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>>) => {
			editSelection.setFlowNodeSelection(flowNodeListModel, nodes);
		},
		[ editSelection ]
	);

	const handleReactFlowLoad = useCallback<IFlowEditor["onReactFlowLoad"]>((_reactFlowInstance) => {
		setReactFlowInstance(_reactFlowInstance);
	}, []);

	const canFlowRemoveElements = useCallback(() => {
		return view.isViewActive();
	}, [ view ]);

	const getNodeInfo = useCallback(
		(model: ISchemaFlowNodeModel<TBlueprintActionNodeTypes>) => {
			if (model.type === "integration") {
				const opts = model.opts.template as ISchemaIntegrationRefModel;
				const int = view.getIntegrationType(opts.integrationType);
				const fn = view.getIntegrationFunction(opts.integrationType, opts.functionName);
				const integrationResource = view.getResourceService().getResourceById(opts.integrationId);

				return {
					typeLabel: int && fn
						? integrationResource?.descriptor.name ? integrationResource?.descriptor.name : t("editor", int.label)
						: undefined,
					elementLabel: int && fn
						? t("editor", fn.label)
						: undefined,
					icon: fn?.icon
				};
			} else if (model.type === NODE_TYPES.ACTION_V2) {
				const actionOpts = model.opts.template as TGetBlueprintSchemaModel<typeof BlueprintActionNode_v2.opts>;
				const resourceDesc = actionOpts.constant && actionOpts.constant.props.action.actionId.constant
					? view.getRelatedResourceDescrption(actionOpts.constant.props.action.actionId.constant.value)
					: undefined;

				return {
					typeLabel: resourceDesc?.name ?? t("editor", "Action")
				};
			} else {
				return {};
			}
		},
		[ view ]
	);

	useSimpleEvent(view.__onFlowEditorZoomIn, () => reactFlowInstance.zoomIn(), [ reactFlowInstance ], true);
	useSimpleEvent(
		view.__onFlowEditorZoomOut,
		() => reactFlowInstance.zoomOut(),
		[ reactFlowInstance ],
		true
	);
	useSimpleEvent(
		view.__onFlowEditorZoomReset,
		() => reactFlowInstance.zoomTo(1.0),
		[ reactFlowInstance ],
		true
	);
	useSimpleEvent(
		view.__onFlowEditorZoomFit,
		() => reactFlowInstance.fitView({ maxZoom: 1.0 }),
		[ reactFlowInstance ],
		true
	);

	useEffect(() => {
		if (reactFlowInstance) {
			reactFlowInstance.fitView({
				maxZoom: 1.0
			});
		}
	}, [ reactFlowInstance ]);

	const baseClass = BEM("edt-action-editor-flow");

	// @todo Translate
	return (
		<Panel style={PANEL_STYLE.NONE} scroll={false} className={baseClass.toClassName()}>
			<FlowEditor
				modelNode={flowNodeListModel}
				layoutType={FLOW_EDITOR_LAYOUT.VERTICAL}
				showMiniMap={false}
				className={baseClass.element("flow-editor").toClassName()}
				onSelectionChange={handleFlowSelectionChange}
				onReactFlowLoad={handleReactFlowLoad}
				getNodeInfo={getNodeInfo as unknown as TGetNodeInfoFn<ISchemaFlowNodeTypeDefinitionMap>}
				canRemoveElements={canFlowRemoveElements}
			/>
		</Panel>
	);
};
